var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useRef, useState } from "react";
import classNames from 'classnames';
import ButtonComponent from './ButtonComponent';
import { Dropdown } from "@widget/popups/Dropdown";
import DropdownIndicator from "@widget/other/DropdownIndicator";
import ActionsListComponent from "@widget/list/Tables/Common/ActionListComponent";
/**
 * @export
 * A component that renders a group of buttons with optional dropdown support.
 *
 * @example
 * ```
 * <ButtonGroup
 *   buttons={[
 *     { id: 'add', title: 'Add', leftIcon: 'fas fa-plus' },
 *     { id: 'edit', title: 'Edit', leftIcon: 'fas fa-edit' }
 *   ]}
 *   activeId="add"
 *   onButtonClick={(id) => console.log('Clicked:', id)}
 * />
 * ```
 *
 * @example
 * ```
 * <ButtonGroup
 *   buttons=[
 *     { id: 'add', title: 'Add', leftIcon: 'fas fa-plus', onClick: () => console.log('Add clicked') },
 *     {
 *       id: 'more',
 *       title: 'More',
 *       leftIcon: 'fas fa-ellipsis-h',
 *       dropdownContent: (
 *         <div>
 *           <div>Option 1</div>
 *           <div>Option 2</div>
 *         </div>
 *       ),
 *       dropdownPosition: 'bottom-right',
 *       showAnchor: true
 *     }
 *   ]
 *   variant="primary"
 *   activeId="add"
 *   className="custom-button-group"
 *   styles={{ margin: '10px' }}
 *   onButtonClick={(id) => console.log('Button clicked:', id)}
 * />
 * ```
 */
const ButtonGroup = ({ buttons, activeId, variant = 'secondary', className, styles, onButtonClick, }) => {
    const dropdownRefsMap = useRef(new Map());
    const [openedDropdown, setOpenedDropdown] = useState(null);
    const getDropdownRef = useCallback((buttonId) => {
        if (!dropdownRefsMap.current.has(buttonId)) {
            dropdownRefsMap.current.set(buttonId, { current: null });
        }
        return dropdownRefsMap.current.get(buttonId);
    }, []);
    const getIsActive = (buttonId, hasDropdown) => {
        if (hasDropdown) {
            return openedDropdown === buttonId;
        }
        return activeId === buttonId;
    };
    const handleButtonClick = (button) => {
        var _a;
        if (button.dropdownContent) {
            onButtonClick === null || onButtonClick === void 0 ? void 0 : onButtonClick(button.id);
            return;
        }
        (_a = button.onClick) === null || _a === void 0 ? void 0 : _a.call(button);
        onButtonClick === null || onButtonClick === void 0 ? void 0 : onButtonClick(button.id);
    };
    const handleDropdownToggle = (buttonId) => {
        setOpenedDropdown(prev => {
            const newOpenedState = prev === buttonId ? null : buttonId;
            return newOpenedState;
        });
    };
    const renderButton = (button, index) => {
        const { id, dropdownContent, dropdownPosition, showAnchor } = button, rest = __rest(button, ["id", "dropdownContent", "dropdownPosition", "showAnchor"]);
        const hasDropdown = !!dropdownContent;
        const isDropdownOpen = openedDropdown === id;
        const buttonElement = (_createElement(ButtonComponent, Object.assign({}, rest, { key: id, active: getIsActive(id, hasDropdown), variant: variant, onClick: () => handleButtonClick(button), className: classNames('uxpcore_buttongroup__item', {
                'uxpcore_buttongroup__item--first': index === 0,
                'uxpcore_buttongroup__item--last': index === buttons.length - 1,
                'uxpcore_buttongroup__item--middle': index > 0 && index < buttons.length - 1,
                'uxpcore_buttongroup__item--dropdown': hasDropdown
            }), leftIcon: button.leftIcon || undefined, rightIcon: button.rightIcon || (hasDropdown ? _jsx(DropdownIndicator, { isOpen: isDropdownOpen }) : undefined) })));
        if (hasDropdown) {
            const dropdownRef = getDropdownRef(id);
            const dropdownContentWithRef = Array.isArray(dropdownContent)
                ? _jsx(ActionsListComponent, { actions: dropdownContent, parentDropdownRef: dropdownRef })
                : dropdownContent;
            return (_jsx(Dropdown, { ref: (ref) => {
                    if (dropdownRef) {
                        dropdownRef.current = ref;
                    }
                }, trigger: buttonElement, content: dropdownContentWithRef, position: dropdownPosition, className: "uxpcore_buttongroup__dropdown", isOpen: isDropdownOpen, onToggle: () => handleDropdownToggle(id), showAnchor: showAnchor }, id));
        }
        return buttonElement;
    };
    return (_jsx("div", { className: classNames('uxpcore_buttongroup', `uxpcore_buttongroup--${variant}`, className), style: styles, children: buttons.map((button, index) => renderButton(button, index)) }));
};
export default ButtonGroup;
